import React, { useEffect, useState } from "react";

import { useGetSpecsTableQuery } from "../../api/wordpress";
import { SpecsTableColumn, SpecsTableRow } from "../../types/SpecsTable.ts";

interface SpecsTableProps {
  postId: number;
}

const SpecsTable: React.FC<SpecsTableProps> = ({ postId }) => {
  const { data, error, isLoading } = useGetSpecsTableQuery(postId);
  // Responsive expanded state only
  const [expanded, setExpanded] = useState<boolean>(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setExpanded(!mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (isLoading) return <div>Loading specs table...</div>;
  if (error) return <div>Failed to load specs table</div>;
  if (!data?.table_rows || !Array.isArray(data.table_rows)) return null;

  // Get max columns count from data
  const maxColumns = Math.max(
    ...data.table_rows.map(
      (row: SpecsTableRow) => row.table_columns?.length || 0,
    ),
  );
  const bodyRows: SpecsTableRow[] = data.table_rows;

  return (
    <div className="specs-table-block pt-5 pb-15 overflow-x-auto">
      <table className="w-full border-collapse min-w-[400px]">
        <thead>
          <tr
            className="cursor-pointer transition bg-primary text-white font-bold hover:bg-primary_soft uppercase font-poppins text-left text-base md:text-lg h-14 md:h-16"
            onClick={() => setExpanded((e) => !e)}
            style={{ minHeight: "56px", touchAction: "manipulation" }}
          >
            {/* Render static header values, then fill remaining columns with empty headers if needed */}
            <th className="py-2 px-3 font-poppins text-left font-bold uppercase bg-primary text-white hover:bg-primary_soft">
              Specifications
            </th>
            <th className="py-2 px-3 font-poppins text-left font-bold uppercase bg-primary text-white hover:bg-primary_soft">
              Factory
            </th>
            <th className="py-2 px-3 font-poppins text-left font-bold uppercase bg-primary text-white hover:bg-primary_soft">
              JMACX GVM
            </th>
            {Array.from({ length: maxColumns - 3 }).map((_, idx) => (
              <th
                key={idx + 3}
                className="py-2 px-3 font-poppins text-left font-bold uppercase bg-primary text-white hover:bg-primary_soft"
              ></th>
            ))}
            <th className="w-8 text-center align-middle py-2">
              <img
                src="/images/chevron-icon.png"
                alt="Toggle Chevron"
                className={`inline-block transition-transform px-1 ${expanded ? "rotate-180" : ""}`}
                style={{ width: "24px", height: "24px", objectFit: "contain" }}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {expanded &&
            bodyRows.map((row, idx) => (
              <tr
                key={idx}
                className={idx % 2 === 0 ? "bg-white" : "bg-[#cccccc]"}
              >
                {/* Render all columns, pad with empty cells if needed */}
                {row.table_columns &&
                  row.table_columns.map(
                    (col: SpecsTableColumn, colIdx: number) => (
                      <td
                        key={colIdx}
                        className="py-2 px-3 font-poppins text-sm md:text-base [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary_soft"
                        dangerouslySetInnerHTML={{ __html: col.column }}
                      />
                    ),
                  )}
                {Array.from({
                  length: maxColumns - (row.table_columns?.length || 0),
                }).map((_, padIdx) => (
                  <td
                    key={"pad" + padIdx}
                    className="py-2 px-3 font-poppins text-sm md:text-base"
                  ></td>
                ))}
                <td className="py-2 px-3 font-poppins" />
              </tr>
            ))}
        </tbody>
      </table>
      {expanded && data.specs_footnote && (
        <div
          className="mt-4 px-3 font-poppins text-sm md:text-base [&_a]:text-primary [&_a]:underline [&_a:hover]:text-primary_soft [&_p+p]:mt-3"
          dangerouslySetInnerHTML={{ __html: data.specs_footnote }}
        />
      )}
    </div>
  );
};

export default SpecsTable;
